import { z } from 'zod';
import { LocalizedStringSchema } from './common';
import { Industry, IndustrySchema, EnabledModules, EnabledModulesSchema } from './tenant';

// ─── Supported MiniApp Engines ───────────────────────────────────────────────

export const MiniAppEngineSchema = z.enum([
  'reservation',   // Date range inventory (hotels, resorts, rentals)
  'appointment',   // Slot & provider/item selection (clinic, spa, beauty, fitness, auto)
  'catalog_order', // Catalog, cart, and ordering (retail, restaurant, ticketing)
  'request_rfp',   // Venue, requirement & quotation request (events, mice, b2b)
  'service_ticket',// Issue reporting & requests (room care, repairs, logistics)
]);
export type MiniAppEngine = z.infer<typeof MiniAppEngineSchema>;

// ─── Field Schema for Custom Intake Fields ───────────────────────────────────

export const MiniAppFieldSchema = z.object({
  key: z.string().regex(/^[a-zA-Z0-9_]+$/),
  type: z.enum(['text', 'textarea', 'number', 'select', 'date', 'phone', 'email']),
  required: z.boolean().default(false),
  label: LocalizedStringSchema,
  placeholder: LocalizedStringSchema.optional(),
  options: z.array(z.object({
    value: z.string(),
    label: LocalizedStringSchema,
  })).optional(),
});
export type MiniAppField = z.infer<typeof MiniAppFieldSchema>;

// ─── MiniApp Service Tab Schema ──────────────────────────────────────────────

export const MiniAppServiceSchema = z.object({
  id: z.string().regex(/^[a-z0-9_-]+$/),
  engine: MiniAppEngineSchema,
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  icon: z.string().default(''),
  label: LocalizedStringSchema,
  outletType: z.string().optional(),
  terminology: z.record(z.string(), LocalizedStringSchema).default({}),
  fields: z.array(MiniAppFieldSchema).default([]),
});
export type MiniAppService = z.infer<typeof MiniAppServiceSchema>;

// ─── MiniApp Tenant Configuration Schema ─────────────────────────────────────

export const MiniAppConfigSchema = z.object({
  version: z.literal(1).default(1),
  defaultServiceId: z.string().optional(),
  terminology: z.record(z.string(), LocalizedStringSchema).default({}),
  services: z.array(MiniAppServiceSchema).default([]),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
});
export type MiniAppConfig = z.infer<typeof MiniAppConfigSchema>;

// ─── Module Prerequisites per Engine ─────────────────────────────────────────

export const ENGINE_REQUIRED_MODULES: Record<MiniAppEngine, (keyof EnabledModules)[]> = {
  reservation: ['book'],
  appointment: ['book'],
  catalog_order: ['catalog'],
  request_rfp: ['book', 'crm'],       // either 'book' or 'crm'
  service_ticket: ['ops', 'support'],  // either 'ops' or 'support'
};

// ─── Industry Default Templates (Supporting all 6 system languages) ──────────

export const INDUSTRY_MINIAPP_TEMPLATES: Record<Industry, MiniAppConfig> = {
  hotel: {
    version: 1,
    defaultServiceId: 'rooms',
    terminology: {
      customer: { th: 'ผู้เข้าพัก', en: 'Guest', zh: '客人', ru: 'Гость', ja: 'ご宿泊者', ko: '투숙객' },
    },
    services: [
      {
        id: 'rooms',
        engine: 'reservation',
        enabled: true,
        sortOrder: 1,
        icon: '🏨',
        label: { th: '🏨 ห้องพัก', en: '🏨 Rooms', zh: '🏨 客房', ru: '🏨 Номера', ja: '🏨 客室', ko: '🏨 객실' },
        terminology: {},
        fields: [],
      },
      {
        id: 'spa',
        engine: 'appointment',
        enabled: true,
        sortOrder: 2,
        icon: '🌿',
        label: { th: '🌿 สปา', en: '🌿 Spa', zh: '🌿 水疗', ru: '🌿 Спа', ja: '🌿 スパ', ko: '🌿 스파' },
        outletType: 'spa',
        terminology: {
          item: { th: 'ทรีตเมนต์', en: 'Treatment', zh: '水疗项目', ru: 'Процедура', ja: 'トリートメント', ko: '트리트먼트' },
          provider: { th: 'เธอราพิสต์', en: 'Therapist', zh: '理疗师', ru: 'Терапевт', ja: 'セラピスト', ko: '테라피스트' },
          location: { th: 'ห้องสปา/สาขา', en: 'Spa Room', zh: '水疗房', ru: 'Спа-кабинет', ja: 'スパルーム', ko: '스파룸' },
        },
        fields: [],
      },
      {
        id: 'restaurant',
        engine: 'catalog_order',
        enabled: true,
        sortOrder: 3,
        icon: '🍽️',
        label: { th: '🍽️ ร้านอาหาร', en: '🍽️ Dining', zh: '🍽️ 餐厅', ru: '🍽️ Ресторан', ja: '🍽️ ダイニング', ko: '🍽️ 다이닝' },
        outletType: 'restaurant',
        terminology: {
          item: { th: 'เมนูอาหาร', en: 'Menu Item', zh: '菜品', ru: 'Блюдо', ja: 'メニュー', ko: '메뉴 항목' },
        },
        fields: [],
      },
      {
        id: 'mice',
        engine: 'request_rfp',
        enabled: true,
        sortOrder: 4,
        icon: '🏛️',
        label: { th: '🏛️ ประชุม-จัดเลี้ยง', en: '🏛️ MICE', zh: '🏛️ 会议宴会', ru: '🏛️ MICE', ja: '🏛️ MICE', ko: '🏛️ MICE' },
        outletType: 'mice',
        terminology: {
          item: { th: 'ห้องประชุม/สถานที่', en: 'Function Room', zh: '会议厅', ru: 'Конференц-зал', ja: '宴会場・会議室', ko: '연회장/회의실' },
        },
        fields: [],
      },
      {
        id: 'room_service',
        engine: 'service_ticket',
        enabled: true,
        sortOrder: 5,
        icon: '🛎️',
        label: { th: '🛎️ บริการในห้อง', en: '🛎️ Room Care', zh: '🛎️ 客房服务', ru: '🛎️ Обслуживание', ja: '🛎️ ルームサービス', ko: '🛎️ 룸 서비스' },
        terminology: {},
        fields: [],
      },
    ],
  },

  clinic: {
    version: 1,
    defaultServiceId: 'appointments',
    terminology: {
      customer: { th: 'คนไข้ / ผู้รับบริการ', en: 'Patient', zh: '患者', ru: 'Пациент', ja: '患者様', ko: '환자' },
    },
    services: [
      {
        id: 'appointments',
        engine: 'appointment',
        enabled: true,
        sortOrder: 1,
        icon: '🩺',
        label: { th: '🩺 นัดตรวจแพทย์', en: '🩺 Doctor Appointment', zh: '🩺 预约就诊', ru: '🩺 Запись к врачу', ja: '🩺 医師の診察予約', ko: '🩺 의사 진료 예약' },
        outletType: 'clinic',
        terminology: {
          item: { th: 'บริการตรวจรักษา', en: 'Medical Service', zh: '诊疗服务', ru: 'Медицинская услуга', ja: '診療サービス', ko: '진료 서비스' },
          provider: { th: 'แพทย์ผู้ตรวจ', en: 'Doctor / Specialist', zh: '主治医生', ru: 'Врач / Специалист', ja: '担当医', ko: '담당 의사' },
          location: { th: 'แผนก/คลินิก', en: 'Department / Clinic', zh: '科室 / 诊所', ru: 'Отделение / Клиника', ja: '診療科 / クリニック', ko: '진료과 / 클리닉' },
        },
        fields: [
          {
            key: 'symptoms',
            type: 'textarea',
            required: false,
            label: { th: 'อาการเบื้องต้น / ประวัติแพ้ยา', en: 'Symptoms / Drug Allergies', zh: '初步症状 / 过敏史', ru: 'Симптомы / Аллергия', ja: '自覚症状 / アレルギー歴', ko: '주요 증상 / 알레르기' },
          },
        ],
      },
      {
        id: 'consultation',
        engine: 'service_ticket',
        enabled: true,
        sortOrder: 2,
        icon: '💬',
        label: { th: '💬 ปรึกษา / สอบถาม', en: '💬 Consultation', zh: '💬 咨询问诊', ru: '💬 Консультация', ja: '💬 医療相談', ko: '💬 상담 및 문의' },
        terminology: {},
        fields: [],
      },
    ],
  },

  beauty: {
    version: 1,
    defaultServiceId: 'booking',
    terminology: {
      customer: { th: 'ลูกค้า', en: 'Client', zh: '顾客', ru: 'Клиент', ja: 'お客様', ko: '고객' },
    },
    services: [
      {
        id: 'booking',
        engine: 'appointment',
        enabled: true,
        sortOrder: 1,
        icon: '✨',
        label: { th: '✨ จองคิวบริการ', en: '✨ Book Service', zh: '✨ 预约美疗', ru: '✨ Запись на услугу', ja: '✨ 施術予約', ko: '✨ 서비스 예약' },
        outletType: 'beauty',
        terminology: {
          item: { th: 'บริการเสริมความงาม', en: 'Beauty Treatment', zh: '美容项目', ru: 'Услуга красоты', ja: '美容メニュー', ko: '뷰티 시술' },
          provider: { th: 'ช่าง / ผู้เชี่ยวชาญ', en: 'Specialist / Stylist', zh: '美容师', ru: 'Мастер / Стилист', ja: 'スタイリスト / 施術者', ko: '전문가 / 스타일리스트' },
          location: { th: 'สาขา', en: 'Branch', zh: '分店', ru: 'Филиал', ja: '店舗・ブース', ko: '지점' },
        },
        fields: [],
      },
      {
        id: 'products',
        engine: 'catalog_order',
        enabled: true,
        sortOrder: 2,
        icon: '🛍️',
        label: { th: '🛍️ สั่งซื้อผลิตภัณฑ์', en: '🛍️ Products', zh: '🛍️ 护肤用品', ru: '🛍️ Косметика', ja: '🛍️ スキンケア製品', ko: '🛍️ 뷰티 제품' },
        outletType: 'shop',
        terminology: {
          item: { th: 'ผลิตภัณฑ์', en: 'Product', zh: '商品', ru: 'Товар', ja: '製品・商品', ko: '제품' },
        },
        fields: [],
      },
    ],
  },

  fitness: {
    version: 1,
    defaultServiceId: 'classes',
    terminology: {
      customer: { th: 'สมาชิก', en: 'Member', zh: '会员', ru: 'Член клуба', ja: 'メンバー', ko: '회원' },
    },
    services: [
      {
        id: 'classes',
        engine: 'appointment',
        enabled: true,
        sortOrder: 1,
        icon: '🏋️',
        label: { th: '🏋️ จองคลาสออกกำลังกาย', en: '🏋️ Book Classes', zh: '🏋️ 课程预约', ru: '🏋️ Занятия', ja: '🏋️ クラス予約', ko: '🏋️ 클래스 예약' },
        outletType: 'fitness_class',
        terminology: {
          item: { th: 'คลาสออกกำลังกาย', en: 'Fitness Class', zh: '健身课程', ru: 'Фитнес-класс', ja: 'フィットネスクラス', ko: '피트니스 클래스' },
          provider: { th: 'เทรนเนอร์ / ผู้ฝึกสอน', en: 'Trainer / Coach', zh: '教练', ru: 'Тренер', ja: 'インストラクター', ko: '트레이너 / 코치' },
          location: { th: 'สตูดิโอ / ยิม', en: 'Studio / Gym', zh: '训练室', ru: 'Студия / Зал', ja: 'スタジオ / ジム', ko: '스튜디오 / 체육관' },
        },
        fields: [],
      },
      {
        id: 'memberships',
        engine: 'catalog_order',
        enabled: true,
        sortOrder: 2,
        icon: '🎫',
        label: { th: '🎫 แพ็กเกจสมาชิก', en: '🎫 Memberships', zh: '🎫 会员套餐', ru: '🎫 Абонементы', ja: '🎫 会員プラン', ko: '🎫 멤버십 패키지' },
        outletType: 'shop',
        terminology: {
          item: { th: 'แพ็กเกจ', en: 'Package', zh: '套餐', ru: 'Пакет', ja: 'プラン', ko: '패키지' },
        },
        fields: [],
      },
    ],
  },

  retail: {
    version: 1,
    defaultServiceId: 'shop',
    terminology: {
      customer: { th: 'ลูกค้า', en: 'Customer', zh: '顾客', ru: 'Покупатель', ja: 'お客様', ko: '고객' },
    },
    services: [
      {
        id: 'shop',
        engine: 'catalog_order',
        enabled: true,
        sortOrder: 1,
        icon: '🛍️',
        label: { th: '🛍️ แคตตาล็อกสินค้า', en: '🛍️ Shop Catalog', zh: '🛍️ 商品目录', ru: '🛍️ Каталог товаров', ja: '🛍️ 商品カタログ', ko: '🛍️ 상품 카탈로그' },
        outletType: 'shop',
        terminology: {
          item: { th: 'สินค้า', en: 'Product', zh: '商品', ru: 'Товар', ja: '商品', ko: '상품' },
        },
        fields: [],
      },
    ],
  },

  restaurant: {
    version: 1,
    defaultServiceId: 'menu',
    terminology: {
      customer: { th: 'ลูกค้า', en: 'Guest / Diner', zh: '食客', ru: 'Гость', ja: 'お客様', ko: '고객' },
    },
    services: [
      {
        id: 'menu',
        engine: 'catalog_order',
        enabled: true,
        sortOrder: 1,
        icon: '🍽️',
        label: { th: '🍽️ เมนูอาหาร & สั่งซื้อ', en: '🍽️ Menu & Order', zh: '🍽️ 点餐服务', ru: '🍽️ Меню и заказ', ja: '🍽️ メニュー注文', ko: '🍽️ 메뉴 및 주문' },
        outletType: 'restaurant',
        terminology: {
          item: { th: 'เมนูอาหาร', en: 'Dish / Item', zh: '菜品', ru: 'Блюдо', ja: 'メニュー', ko: '메뉴' },
        },
        fields: [],
      },
      {
        id: 'reservation',
        engine: 'appointment',
        enabled: true,
        sortOrder: 2,
        icon: '🪑',
        label: { th: '🪑 สำรองโต๊ะล่วงหน้า', en: '🪑 Table Booking', zh: '🪑 预订座位', ru: '🪑 Бронь столика', ja: '🪑 席の予約', ko: '🪑 테이블 예약' },
        outletType: 'restaurant',
        terminology: {
          item: { th: 'โซนที่นั่ง', en: 'Seating Area', zh: '就餐区域', ru: 'Зона посадки', ja: '席エリア', ko: '좌석 구역' },
          location: { th: 'สาขาห้องอาหาร', en: 'Branch', zh: '分店', ru: 'Филиал', ja: '店舗', ko: '지점' },
        },
        fields: [],
      },
    ],
  },

  education: {
    version: 1,
    defaultServiceId: 'trial_class',
    terminology: {
      customer: { th: 'นักเรียน / ผู้ปกครอง', en: 'Student / Parent', zh: '学生/家长', ru: 'Студент / Родитель', ja: '生徒 / 保護者', ko: '학생 / 학부모' },
    },
    services: [
      {
        id: 'trial_class',
        engine: 'appointment',
        enabled: true,
        sortOrder: 1,
        icon: '📚',
        label: { th: '📚 จองคลาสเรียนทดลอง', en: '📚 Trial Class', zh: '📚 试听课程', ru: '📚 Пробный урок', ja: '📚 体験レッスン', ko: '📚 체험 수업 예약' },
        outletType: 'class',
        terminology: {
          item: { th: 'คอร์สเรียน', en: 'Course', zh: '课程', ru: 'Курс', ja: 'コース', ko: '코스' },
          provider: { th: 'อาจารย์ผู้สอน', en: 'Instructor / Teacher', zh: '讲师', ru: 'Преподаватель', ja: '講師', ko: '강사 / 교사' },
          location: { th: 'ห้องเรียน / สาขา', en: 'Classroom / Campus', zh: '教室/校区', ru: 'Аудитория / Кампус', ja: '教室 / キャンパス', ko: '강의실 / 캠퍼스' },
        },
        fields: [],
      },
      {
        id: 'enquiry',
        engine: 'request_rfp',
        enabled: true,
        sortOrder: 2,
        icon: '📝',
        label: { th: '📝 สอบถามข้อมูลคอร์ส', en: '📝 Course Enquiry', zh: '📝 课程咨询', ru: '📝 Запрос курса', ja: '📝 コース相談', ko: '📝 과정 문의' },
        terminology: {},
        fields: [],
      },
    ],
  },

  association: {
    version: 1,
    defaultServiceId: 'cpd_seminar',
    terminology: {
      customer: { th: 'สมาชิก / ผู้เข้าร่วมสัมมนา', en: 'Member / Attendee', zh: '会员/参会人员', ru: 'Член ассоциации / Участник', ja: '会員 / 参加者', ko: '회원 / 참가자' },
    },
    services: [
      {
        id: 'cpd_seminar',
        engine: 'appointment',
        enabled: true,
        sortOrder: 1,
        icon: '🎓',
        label: { th: '🎓 อบรมสัมมนาวิชาชีพ (CPD)', en: '🎓 CPD Seminar & Training', zh: '🎓 CPD专业培训研讨会', ru: '🎓 Семинары и тренинги CPD', ja: '🎓 専門研修・セミナー (CPD)', ko: '🎓 전문 연수 및 세미나 (CPD)' },
        outletType: 'seminar',
        terminology: {
          item: { th: 'หลักสูตร / หัวข้อสัมมนา', en: 'Course / Topic', zh: '课程/研讨主题', ru: 'Курс / Тема семинара', ja: '研修コース / セミナー議題', ko: '연수 과정 / 세미나 주제' },
          provider: { th: 'วิทยากร / ผู้บรรยาย', en: 'Speaker / Lecturer', zh: '主讲人/讲师', ru: 'Спикер / Лектор', ja: '講師 / 講演者', ko: '강사 / 강연자' },
          location: { th: 'ห้องสัมมนา / รูปแบบอบรม', en: 'Seminar Room / Mode', zh: '研讨厅/培训模式', ru: 'Зал / Формат участия', ja: 'セミナー室 / 開催形式', ko: '세미나실 / 연수 형태' },
        },
        fields: [],
      },
      {
        id: 'member_services',
        engine: 'request_rfp',
        enabled: true,
        sortOrder: 2,
        icon: '🏛️',
        label: { th: '🏛️ บริการสมาชิกและยื่นคำขอ', en: '🏛️ Member Services & Requests', zh: '🏛️ 会员服务与申请', ru: '🏛️ Услуги для членов и запросы', ja: '🏛️ 会員サービス・各種申請', ko: '🏛️ 회원 서비스 및 신청' },
        terminology: {},
        fields: [],
      },
    ],
  },

  automotive: {
    version: 1,
    defaultServiceId: 'service_booking',
    terminology: {
      customer: { th: 'เจ้าของรถ / ลูกค้า', en: 'Vehicle Owner', zh: '车主', ru: 'Автовладелец', ja: 'オーナー様', ko: '차량 소유주' },
    },
    services: [
      {
        id: 'service_booking',
        engine: 'appointment',
        enabled: true,
        sortOrder: 1,
        icon: '🚗',
        label: { th: '🚗 นัดหมายเข้าศูนย์บริการ', en: '🚗 Service Booking', zh: '🚗 预约保养维修', ru: '🚗 Запись на ТО', ja: '🚗 入庫・点検予約', ko: '🚗 정비 예약' },
        outletType: 'auto_service',
        terminology: {
          item: { th: 'รายการเช็คระยะ/บริการ', en: 'Service Package', zh: '保养套餐', ru: 'Пакет ТО', ja: '点検・整備項目', ko: '점검/정비 서비스' },
          provider: { th: 'ช่างผู้เชี่ยวชาญ', en: 'Technician', zh: '技师', ru: 'Мастер-механик', ja: '整備士', ko: '정비사' },
          location: { th: 'ศูนย์บริการ / สาขา', en: 'Service Center / Branch', zh: '服务中心', ru: 'Сервисный центр', ja: 'サービス工場', ko: '서비스 센터' },
        },
        fields: [
          {
            key: 'plate_number',
            type: 'text',
            required: true,
            label: { th: 'ทะเบียนรถและรุ่น', en: 'License Plate & Model', zh: '车牌号与车型', ru: 'Госномер и модель', ja: 'ナンバープレート・車種', ko: '차량 번호 및 차종' },
          },
        ],
      },
      {
        id: 'quote',
        engine: 'request_rfp',
        enabled: true,
        sortOrder: 2,
        icon: '📋',
        label: { th: '📋 ขอใบเสนอราคาซ่อม', en: '📋 Repair Quotation', zh: '📋 维修报价', ru: '📋 Расчет стоимости', ja: '📋 見積もり依頼', ko: '📋 수리 견적 요청' },
        terminology: {},
        fields: [],
      },
    ],
  },

  service_repair: {
    version: 1,
    defaultServiceId: 'ticket',
    terminology: {
      customer: { th: 'ผู้แจ้งซ่อม / ลูกค้า', en: 'Customer', zh: '客户', ru: 'Клиент', ja: 'お客様', ko: '고객' },
    },
    services: [
      {
        id: 'ticket',
        engine: 'service_ticket',
        enabled: true,
        sortOrder: 1,
        icon: '🔧',
        label: { th: '🔧 แจ้งซ่อมบำรุง', en: '🔧 Request Repair', zh: '🔧 报修申请', ru: '🔧 Заявка на ремонт', ja: '🔧 修理・メンテナンス依頼', ko: '🔧 수리 및 유지보수 신청' },
        terminology: {},
        fields: [],
      },
      {
        id: 'technician',
        engine: 'appointment',
        enabled: true,
        sortOrder: 2,
        icon: '📅',
        label: { th: '📅 นัดหมายช่างเข้าตรวจ', en: '📅 Schedule Technician', zh: '📅 预约上门', ru: '📅 Вызов мастера', ja: '📅 訪問日時予約', ko: '📅 기사 방문 예약' },
        outletType: 'technician',
        terminology: {
          item: { th: 'ประเภทงานซ่อม', en: 'Repair Service', zh: '维修项目', ru: 'Вид ремонта', ja: '修理種別', ko: '수리 항목' },
          provider: { th: 'ช่างผู้ดูแล', en: 'Technician', zh: '师傅', ru: 'Мастер', ja: '担当技術者', ko: '담당 기사' },
          location: { th: 'พื้นที่บริการ', en: 'Service Area', zh: '服务区域', ru: 'Зона обслуживания', ja: '対象エリア', ko: '서비스 지역' },
        },
        fields: [],
      },
    ],
  },

  events: {
    version: 1,
    defaultServiceId: 'venue',
    terminology: {
      customer: { th: 'ผู้จัดงาน / ลูกค้า', en: 'Organizer / Client', zh: '主办方', ru: 'Организатор', ja: '主催者様', ko: '행사 주최자' },
    },
    services: [
      {
        id: 'venue',
        engine: 'request_rfp',
        enabled: true,
        sortOrder: 1,
        icon: '🏛️',
        label: { th: '🏛️ จองสถานที่ / ขอใบเสนอราคา', en: '🏛️ Venue Booking & RFP', zh: '🏛️ 场地预订与报价', ru: '🏛️ Аренда площадки', ja: '🏛️ 会場予約・見積もり', ko: '🏛️ 장소 예약 및 견적' },
        outletType: 'venue',
        terminology: {
          item: { th: 'ฮอลล์ / สถานที่จัดงาน', en: 'Hall / Venue', zh: '场地展厅', ru: 'Зал / Площадка', ja: 'ホール / 会場', ko: '홀 / 행사장' },
        },
        fields: [],
      },
      {
        id: 'tickets',
        engine: 'catalog_order',
        enabled: true,
        sortOrder: 2,
        icon: '🎟️',
        label: { th: '🎟️ จองบัตรเข้างาน', en: '🎟️ Tickets', zh: '🎟️ 门票购买', ru: '🎟️ Билеты', ja: '🎟️ チケット購入', ko: '🎟️ 입장권 예매' },
        outletType: 'ticket',
        terminology: {
          item: { th: 'บัตรเข้างาน', en: 'Ticket', zh: '门票', ru: 'Билет', ja: 'チケット', ko: '티켓' },
        },
        fields: [],
      },
    ],
  },

  real_estate: {
    version: 1,
    defaultServiceId: 'viewing',
    terminology: {
      customer: { th: 'ผู้สนใจ / ลูกบ้าน', en: 'Client / Resident', zh: '看房客户/业主', ru: 'Клиент / Жилец', ja: 'お客様 / 入居者様', ko: '고객 / 입주민' },
    },
    services: [
      {
        id: 'viewing',
        engine: 'request_rfp',
        enabled: true,
        sortOrder: 1,
        icon: '🏡',
        label: { th: '🏡 นัดชมโครงการ', en: '🏡 Schedule Viewing', zh: '🏡 预约看房', ru: '🏡 Просмотр объекта', ja: '🏡 内見・見学予約', ko: '🏡 방문 예약' },
        outletType: 'property',
        terminology: {
          item: { th: 'แบบบ้าน / ยูนิต', en: 'Property / Unit', zh: '房型单元', ru: 'Объект / Квартира', ja: '物件・部屋タイプ', ko: '부동산 / 호수' },
        },
        fields: [],
      },
      {
        id: 'resident_care',
        engine: 'service_ticket',
        enabled: true,
        sortOrder: 2,
        icon: '🏢',
        label: { th: '🏢 บริการลูกบ้าน / แจ้งซ่อม', en: '🏢 Resident Care', zh: '🏢 物业报修与服务', ru: '🏢 Сервис для жильцов', ja: '🏢 入居者サポート', ko: '🏢 입주민 케어 및 수리' },
        terminology: {},
        fields: [],
      },
    ],
  },

  corporate: {
    version: 1,
    defaultServiceId: 'rfp',
    terminology: {
      customer: { th: 'ลูกค้าองค์กร', en: 'Corporate Client', zh: '企业客户', ru: 'Корпоративный клиент', ja: '法人のお客様', ko: '기업 고객' },
    },
    services: [
      {
        id: 'rfp',
        engine: 'request_rfp',
        enabled: true,
        sortOrder: 1,
        icon: '💼',
        label: { th: '💼 ขอใบเสนอราคาองค์กร', en: '💼 Request Quotation', zh: '💼 企业报价咨询', ru: '💼 Запрос предложения', ja: '💼 法人向け見積依頼', ko: '💼 기업 견적 요청' },
        terminology: {},
        fields: [],
      },
    ],
  },

  logistics: {
    version: 1,
    defaultServiceId: 'pickup_request',
    terminology: {
      customer: { th: 'ผู้ส่ง / ผู้รับพัสดุ', en: 'Shipper / Consignee', zh: '寄件人/收件人', ru: 'Отправитель / Получатель', ja: 'ご依頼主様', ko: '발송인 / 수취인' },
    },
    services: [
      {
        id: 'pickup_request',
        engine: 'service_ticket',
        enabled: true,
        sortOrder: 1,
        icon: '📦',
        label: { th: '📦 เรียกรถรับพัสดุ / แจ้งส่ง', en: '📦 Request Pickup', zh: '📦 预约取件', ru: '📦 Заявка на забор груза', ja: '📦 集荷依頼 / 発送', ko: '📦 픽업 요청 / 발송' },
        terminology: {},
        fields: [],
      },
    ],
  },

  entertainment: {
    version: 1,
    defaultServiceId: 'tickets',
    terminology: {
      customer: { th: 'ผู้เข้าชม / ผู้เล่น', en: 'Visitor / Player', zh: '游客/玩家', ru: 'Посетитель / Игрок', ja: '来場者様 / プレイヤー', ko: '방문객 / 플레이어' },
    },
    services: [
      {
        id: 'tickets',
        engine: 'catalog_order',
        enabled: true,
        sortOrder: 1,
        icon: '🎟️',
        label: { th: '🎟️ ซื้อตั๋ว / บัตรผ่าน', en: '🎟️ Admission Tickets', zh: '🎟️ 门票通行证', ru: '🎟️ Билеты / Абонементы', ja: '🎟️ 入場チケット', ko: '🎟️ 입장권 구매' },
        outletType: 'ticket',
        terminology: {
          item: { th: 'ตั๋ว/รอบกิจกรรม', en: 'Ticket / Pass', zh: '门票项目', ru: 'Билет / Сеанс', ja: 'チケット種別', ko: '티켓 / 패스' },
        },
        fields: [],
      },
      {
        id: 'session',
        engine: 'appointment',
        enabled: true,
        sortOrder: 2,
        icon: '⛳',
        label: { th: '⛳ จองรอบกิจกรรม / สนาม', en: '⛳ Book Session / Tee Time', zh: '⛳ 预约场次/球道', ru: '⛳ Бронь сеанса / Игры', ja: '⛳ プレー枠・セッション予約', ko: '⛳ 세션 / 경기 예약' },
        outletType: 'session',
        terminology: {
          item: { th: 'สนาม/รอบเล่น', en: 'Court / Session', zh: '场地/场次', ru: 'Поле / Сеанс', ja: 'コート / 枠', ko: '코트 / 세션' },
          location: { th: 'สนาม / สาขา', en: 'Venue / Facility', zh: '场馆', ru: 'Спортивный комплекс', ja: '施設 / ブース', ko: '경기장 / 시설' },
        },
        fields: [],
      },
    ],
  },

  custom: {
    version: 1,
    defaultServiceId: 'general_service',
    terminology: {
      customer: { th: 'ลูกค้า', en: 'Customer', zh: '客户', ru: 'Клиент', ja: 'お客様', ko: '고객' },
    },
    services: [
      {
        id: 'general_service',
        engine: 'appointment',
        enabled: true,
        sortOrder: 1,
        icon: '📋',
        label: { th: '📋 จองรับบริการ', en: '📋 Book Service', zh: '📋 服务预约', ru: '📋 Запись на услугу', ja: '📋 サービス予約', ko: '📋 서비스 예약' },
        outletType: 'general',
        terminology: {
          item: { th: 'บริการ', en: 'Service', zh: '服务项目', ru: 'Услуга', ja: 'サービス項目', ko: '서비스' },
          provider: { th: 'ผู้ให้บริการ', en: 'Provider / Staff', zh: '服务人员', ru: 'Специалист', ja: '担当スタッフ', ko: '제공자 / 스태프' },
          location: { th: 'สถานที่ / สาขา', en: 'Location / Branch', zh: '服务地点', ru: 'Локация / Филиал', ja: '所在地 / 支店', ko: '위치 / 지점' },
        },
        fields: [],
      },
    ],
  },
};

// ─── Pure Function to Resolve MiniApp Configuration ──────────────────────────

export function resolveMiniAppConfig(
  industry: Industry,
  storedConfig: unknown,
  enabledModules: Partial<EnabledModules> = {}
): MiniAppConfig {
  const safeIndustry = IndustrySchema.safeParse(industry).success ? industry : 'custom';
  const template = INDUSTRY_MINIAPP_TEMPLATES[safeIndustry] || INDUSTRY_MINIAPP_TEMPLATES.custom;

  let candidate: MiniAppConfig;
  const parsed = MiniAppConfigSchema.safeParse(storedConfig);
  if (parsed.success && parsed.data.services.length > 0) {
    candidate = parsed.data;
  } else {
    candidate = JSON.parse(JSON.stringify(template));
  }

  // Filter services based on tenant enabledModules
  const filteredServices = candidate.services.filter((svc) => {
    if (!svc.enabled) return false;
    const requiredList = ENGINE_REQUIRED_MODULES[svc.engine];
    if (!requiredList || requiredList.length === 0) return true;
    // Any-of requirement logic (e.g. ['ops', 'support'] -> at least one must be enabled)
    return requiredList.some((modKey) => Boolean(enabledModules[modKey]));
  });

  return {
    ...candidate,
    services: filteredServices,
    defaultServiceId: filteredServices.some((s) => s.id === candidate.defaultServiceId)
      ? candidate.defaultServiceId
      : filteredServices[0]?.id || undefined,
  };
}
